// External imports
import type { RootState } from 'src/redux/store';

import { useCallback } from 'react';

import { removeTenantIdLS } from 'src/utils/localStorageUtils';

import { useAppDispatch, useAppSelector } from 'src/hook/hooks';
import { setAuthStateInitial } from 'src/pages/auth/slice/authSlice';
import { useLogoutUserMutation } from 'src/pages/auth/slice/authApi';
import { setChatStateInitial } from 'src/pages/chat/slice/chatSlice';
import { useCallContext } from 'src/pages/chat/hooks/use-call-context';
import { resetSettingState } from 'src/pages/setting/slice/settingSlice';
import { setChannelStateInitial } from 'src/pages/channel/slice/channelSlice';

import { useAuthContext } from 'src/auth/hooks';
import { signOut } from 'src/auth/context/jwt/action';

export const useLogout = () => {
  const dispatch = useAppDispatch();
  const { handleResetCallDetails, disconnectSIP } = useCallContext();
  const { checkUserSession } = useAuthContext();

  const { loginApiData } = useAppSelector(
    (state: RootState & { auth: RootState['auth'] }) => state.auth
  );

  const [logoutUser, { data: logoutApiResp, isLoading: logoutApiLoader, error: logoutApiError }] =
    useLogoutUserMutation();

  const logoutHandler = useCallback(async () => {
    try {
      // API logout (if email exists)
      if (loginApiData?.email) {
        await logoutUser({ email: loginApiData.email }).unwrap();
      }

      // Reset JWT session
      await signOut();

      // Reset Redux states
      dispatch(setAuthStateInitial());
      dispatch(setChatStateInitial());
      dispatch(setChannelStateInitial());
      dispatch(resetSettingState());

      // Reset SIP
      handleResetCallDetails();
      disconnectSIP();

      // Remove tenant from local storage
      removeTenantIdLS();

      // Re-check user session
      await checkUserSession?.();
    } catch (error) {
      console.error('Logout failed', error);
      throw error; // Optional: let consuming component handle the error
    }
  }, [
    loginApiData?.email,
    logoutUser,
    dispatch,
    handleResetCallDetails,
    disconnectSIP,
    checkUserSession,
  ]);

  return { logoutHandler, isLoading: logoutApiLoader, error: logoutApiError, logoutApiResp };
};
